import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  fetchOpenRequests,
  normalizeOpenRequestEntry,
  sendPermissionResponse,
  type OpenRequestEntry,
} from '../../api/ws'
import { sendPermissionResponse as sendPermissionResponseHTTP } from '../../api/client'
import { PermissionRequestCard } from '../permission/PermissionRequestCard'
import type {
  PermissionDecision,
  PermissionDecisionBehavior,
} from '../../store/types'

// The axis-D PULL SURFACE (X2, docs/authz-policy-model.md §3.5 property 4).
//
// The ladder decides who gets PUSHED; this is where anyone in the household
// BROWSES every open request and — where they're in the approver set B — acts
// on one they were never paged for. Distinct from the Attention inbox's
// approvals (AttentionSheet), the narrow "what I've been paged for" list.
//
// Each row carries two per-caller flags from the server:
//   • actionable_now — your rung is live (you were paged). "Waiting on you."
//   • can_act        — you're in B; you may act even if never paged. "You can approve."
// Neither → view-only (reads are open, but you're not an approver here).

// Friendly labels for the coarse domain key the server returns (the gate
// action's namespace). Unknown domains fall back to the raw key.
const DOMAIN_LABELS: Record<string, string> = {
  tool: 'Tool calls',
  host: 'Host',
  secret: 'Secrets',
  external_resource: 'Connections',
  ai_agent_instance: 'Agents',
  organization_position: 'Positions',
  working_directory: 'Working dirs',
  organization: 'Household',
  organization_member: 'Members',
  agent_job_description: 'Roles',
  ai_brain_model: 'Models',
}

function domainLabel(d: string): string {
  return DOMAIN_LABELS[d] ?? d
}

type StatusFilter = 'all' | 'can_act' | 'waiting'

export function OpenRequestsSheet() {
  const [entries, setEntries] = useState<OpenRequestEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [domain, setDomain] = useState<string>('all')
  const [agent, setAgent] = useState<string>('all')
  const [status, setStatus] = useState<StatusFilter>('all')

  // The request currently being acted on (a can_act row was tapped). When set,
  // the card replaces the list until the decision resolves or the user backs out.
  const [acting, setActing] = useState<OpenRequestEntry | null>(null)
  const [sendError, setSendError] = useState(false)

  function load() {
    setLoading(true)
    fetchOpenRequests()
      .then((r) => {
        if (r.error) {
          setError(r.error)
          return
        }
        setError(null)
        setEntries(r.entries ?? [])
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    let cancelled = false
    fetchOpenRequests()
      .then((r) => {
        if (cancelled) return
        if (r.error) {
          setError(r.error)
          return
        }
        setEntries(r.entries ?? [])
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Distinct filter options, derived from the loaded set so we only ever offer
  // chips that match something.
  const domains = useMemo(() => {
    const set = new Set(entries.map((e) => e.domain).filter(Boolean))
    return Array.from(set).sort()
  }, [entries])

  const agents = useMemo(() => {
    const set = new Set(
      entries.map((e) => e.agent_name).filter((n): n is string => !!n),
    )
    return Array.from(set).sort()
  }, [entries])

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (domain !== 'all' && e.domain !== domain) return false
      if (agent !== 'all' && e.agent_name !== agent) return false
      if (status === 'can_act' && !e.can_act) return false
      if (status === 'waiting' && !e.actionable_now) return false
      return true
    })
  }, [entries, domain, agent, status])

  async function respond(
    decision: PermissionDecision,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) {
    if (!acting) return
    const reqID = acting.request_id
    setSendError(false)
    const args = {
      requestID: reqID,
      behavior: decision.behavior,
      expiresAt: decision.expires_at,
      interrupt: decision.interrupt,
      selectedOptionID: extras?.selectedOptionID,
      freeText: extras?.freeText,
    }
    // WS primary, HTTP /response fallback — only remove the row on a confirmed
    // send so a lost response is never silently dropped (mirrors the inbox).
    const delivered = sendPermissionResponse(args)
    if (!delivered) {
      try {
        await sendPermissionResponseHTTP(args)
      } catch {
        setSendError(true)
        return
      }
    }
    setEntries((prev) => prev.filter((e) => e.request_id !== reqID))
    setActing(null)
  }

  function shortcut(
    behavior: PermissionDecisionBehavior,
    extras?: { selectedOptionID?: string; freeText?: string },
  ) {
    void respond(
      { behavior, expires_at: null, interrupt: behavior === 'deny_stop' },
      extras,
    )
  }

  if (loading) {
    return (
      <div style={{ padding: '24px 16px', textAlign: 'center', opacity: 0.6 }}>
        Loading open requests…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <div
          style={{
            background: '#fff0f0',
            border: '1px solid #f5a5a5',
            color: '#900',
            padding: 12,
            borderRadius: 4,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  // Act flow — a can_act row was tapped; the card takes over the sheet.
  if (acting) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            padding: '8px 16px',
            borderBottom: '1px solid var(--separator, #eee)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button
            onClick={() => {
              setActing(null)
              setSendError(false)
            }}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 15,
              color: 'var(--tint, #2563eb)',
              padding: '4px 6px',
            }}
          >
            ‹ Back
          </button>
          <span style={{ fontSize: 13, opacity: 0.7 }}>Review request</span>
        </div>
        {sendError && (
          <div
            role="alert"
            style={{
              margin: '8px 16px 0',
              background: '#fff0f0',
              border: '1px solid #f5a5a5',
              color: '#900',
              padding: 10,
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            Couldn’t reach the server — your response wasn’t sent. Try again.
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <PermissionRequestCard
            request={normalizeOpenRequestEntry(acting)}
            onShortcut={shortcut}
            onAlwaysAllow={(expires_at) =>
              void respond({ behavior: 'always_allow', expires_at, interrupt: false })
            }
            onAlwaysDeny={(expires_at) =>
              void respond({ behavior: 'always_deny', expires_at, interrupt: false })
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <FilterBar
        domains={domains}
        agents={agents}
        domain={domain}
        agent={agent}
        status={status}
        onDomain={setDomain}
        onAgent={setAgent}
        onStatus={setStatus}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 40 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {entries.length === 0 ? 'No open requests' : 'Nothing matches'}
            </div>
            <div style={{ opacity: 0.6, fontSize: 14, maxWidth: 280 }}>
              {entries.length === 0
                ? 'Nothing is waiting anywhere in this household.'
                : 'Try clearing a filter.'}
            </div>
            <button onClick={load} style={secondaryBtn}>
              Refresh
            </button>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filtered.map((e) => (
              <OpenRequestRow
                key={e.request_id}
                entry={e}
                onAct={() => e.can_act && setActing(e)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function FilterBar(props: {
  domains: string[]
  agents: string[]
  domain: string
  agent: string
  status: StatusFilter
  onDomain: (d: string) => void
  onAgent: (a: string) => void
  onStatus: (s: StatusFilter) => void
}) {
  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid var(--separator, #eee)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <Segmented
        value={props.status}
        options={[
          { value: 'all', label: 'All' },
          { value: 'waiting', label: 'Waiting on you' },
          { value: 'can_act', label: 'You can act' },
        ]}
        onChange={(v) => props.onStatus(v as StatusFilter)}
      />
      {props.domains.length > 1 && (
        <ChipRow
          value={props.domain}
          options={[
            { value: 'all', label: 'All types' },
            ...props.domains.map((d) => ({ value: d, label: domainLabel(d) })),
          ]}
          onChange={props.onDomain}
        />
      )}
      {props.agents.length > 1 && (
        <ChipRow
          value={props.agent}
          options={[
            { value: 'all', label: 'All agents' },
            ...props.agents.map((a) => ({ value: a, label: a })),
          ]}
          onChange={props.onAgent}
        />
      )}
    </div>
  )
}

function Segmented(props: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div
      style={{
        display: 'flex',
        background: 'var(--fill-tertiary, #f0f0f2)',
        borderRadius: 8,
        padding: 2,
      }}
    >
      {props.options.map((o) => {
        const on = o.value === props.value
        return (
          <button
            key={o.value}
            onClick={() => props.onChange(o.value)}
            style={{
              flex: 1,
              border: 'none',
              borderRadius: 6,
              padding: '6px 4px',
              fontSize: 13,
              fontWeight: on ? 600 : 400,
              cursor: 'pointer',
              background: on ? 'var(--bg-elevated, #fff)' : 'transparent',
              color: on ? 'var(--label, #000)' : 'var(--label-secondary, #666)',
              boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function ChipRow(props: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
      {props.options.map((o) => {
        const on = o.value === props.value
        return (
          <button
            key={o.value}
            onClick={() => props.onChange(o.value)}
            style={{
              flexShrink: 0,
              border: '1px solid var(--separator, #ddd)',
              borderRadius: 999,
              padding: '4px 12px',
              fontSize: 13,
              cursor: 'pointer',
              background: on ? 'var(--tint, #2563eb)' : 'transparent',
              color: on ? '#fff' : 'var(--label-secondary, #666)',
              borderColor: on ? 'var(--tint, #2563eb)' : 'var(--separator, #ddd)',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function OpenRequestRow(props: { entry: OpenRequestEntry; onAct: () => void }) {
  const e = props.entry
  const canAct = e.can_act
  const subtitleParts = [e.agent_name, e.position_name].filter(Boolean)
  return (
    <li
      style={{
        borderBottom: '1px solid var(--separator, #eee)',
      }}
    >
      <button
        onClick={props.onAct}
        disabled={!canAct}
        style={{
          width: '100%',
          textAlign: 'left',
          border: 'none',
          background: 'transparent',
          padding: '12px 16px',
          cursor: canAct ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          opacity: canAct ? 1 : 0.7,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {e.tool_name || 'Request'}
          </div>
          {subtitleParts.length > 0 && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--label-secondary, #666)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitleParts.join(' · ')}
            </div>
          )}
          <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
            <DomainBadge domain={e.domain} />
            <StatusBadge canAct={e.can_act} actionableNow={e.actionable_now} />
          </div>
        </div>
        {canAct && (
          <span style={{ color: 'var(--label-tertiary, #999)', fontSize: 18 }}>›</span>
        )}
      </button>
    </li>
  )
}

function DomainBadge(props: { domain: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--label-secondary, #666)',
        background: 'var(--fill-tertiary, #f0f0f2)',
        borderRadius: 4,
        padding: '2px 6px',
      }}
    >
      {domainLabel(props.domain)}
    </span>
  )
}

function StatusBadge(props: { canAct: boolean; actionableNow: boolean }) {
  let label: string
  let color: string
  let bg: string
  if (props.actionableNow) {
    label = 'Waiting on you'
    color = '#8a5a00'
    bg = '#fff3d6'
  } else if (props.canAct) {
    label = 'You can approve'
    color = '#1a6a2e'
    bg = '#dcf5e3'
  } else {
    label = 'View only'
    color = 'var(--label-tertiary, #999)'
    bg = 'transparent'
  }
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color,
        background: bg,
        borderRadius: 4,
        padding: '2px 6px',
      }}
    >
      {label}
    </span>
  )
}

const secondaryBtn: CSSProperties = {
  marginTop: 4,
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid var(--separator, #ddd)',
  background: 'transparent',
  fontSize: 15,
  cursor: 'pointer',
  color: 'var(--label, inherit)',
}
